import * as React from "react";
import { Controller, UseFormReturn } from "react-hook-form";

import { Table, RadioGroup } from "@/components/ui";

interface PermissionItem {
  id: string;
  displayLabel: string;
}

interface PermissionGroupTableProps {
  category: string;
  items: PermissionItem[];
  form: UseFormReturn<any>;
  isDisabled: boolean;
  changeCategoryPermissions: (items: { id: string }[], level: "NONE" | "READ" | "WRITE") => void;
}

export const PermissionGroupTable: React.FC<PermissionGroupTableProps> = ({
  category,
  items,
  form,
  isDisabled,
  changeCategoryPermissions,
}) => {
  return (
    <div className="w-full border border-slate-200 rounded-lg overflow-hidden shadow-sm bg-white">
      <Table>
        <Table.Header>
          <Table.Row className="bg-slate-50 hover:bg-slate-50 border-b border-slate-200">
            <Table.Head className="w-[225px] min-w-[225px] max-w-[225px] font-bold text-slate-800 text-sm pl-4 whitespace-nowrap">
              {category}
            </Table.Head>
            <Table.Head className="text-center font-bold text-slate-700 whitespace-nowrap">
              <div className="flex justify-center py-1">
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={() => changeCategoryPermissions(items, "NONE")}
                  className="text-[11px] uppercase tracking-wider text-slate-500 hover:text-blue-600 hover:bg-blue-50/70 cursor-pointer font-bold py-1.5 px-3 rounded-md transition-all focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                  title={`Set all ${category} to None`}
                >
                  None
                </button>
              </div>
            </Table.Head>
            <Table.Head className="text-center font-bold text-slate-700 whitespace-nowrap">
              <div className="flex justify-center py-1">
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={() => changeCategoryPermissions(items, "READ")}
                  className="text-[11px] uppercase tracking-wider text-slate-500 hover:text-blue-600 hover:bg-blue-50/70 cursor-pointer font-bold py-1.5 px-3 rounded-md transition-all focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                  title={`Set all ${category} to Read`}
                >
                  Read
                </button>
              </div>
            </Table.Head>
            <Table.Head className="text-center font-bold text-slate-700 whitespace-nowrap">
              <div className="flex justify-center py-1">
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={() => changeCategoryPermissions(items, "WRITE")}
                  className="text-[11px] uppercase tracking-wider text-slate-500 hover:text-blue-600 hover:bg-blue-50/70 cursor-pointer font-bold py-1.5 px-3 rounded-md transition-all focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                  title={`Set all ${category} to Write`}
                >
                  Write
                </button>
              </div>
            </Table.Head>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {items.map(item => (
            <Table.Row key={item.id} className="hover:bg-slate-50/30 transition-colors">
              <Table.Cell
                className="w-[225px] min-w-[225px] max-w-[225px] text-sm font-medium text-slate-700 pl-4 whitespace-nowrap truncate"
                title={item.displayLabel}
              >
                {item.displayLabel}
              </Table.Cell>
              <Table.Cell colSpan={3} className="p-0">
                <Controller
                  name={`permissionsMap.${item.id}`}
                  control={form.control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value}
                      onValueChange={val => {
                        field.onChange(val);
                      }}
                      disabled={isDisabled}
                      className="grid grid-cols-3 w-full py-3 h-full items-center justify-items-center"
                    >
                      <div className="flex justify-center">
                        <RadioGroup.Item value="NONE" />
                      </div>
                      <div className="flex justify-center">
                        <RadioGroup.Item value="READ" />
                      </div>
                      <div className="flex justify-center">
                        <RadioGroup.Item value="WRITE" />
                      </div>
                    </RadioGroup>
                  )}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};
